import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { NewCaseDialog } from "@/components/IntakeDialog";
import { Cpu, MapPin, Radio, Snowflake, CircleDot, ChevronRight } from "lucide-react";

const PRIORITY_STYLES = {
  critical: "text-monk-critical border-monk-critical/40 bg-monk-critical/10",
  high: "text-monk-warn border-monk-warn/40 bg-monk-warn/10",
  medium: "text-monk-blue border-monk-blue/40 bg-monk-blue/10",
  low: "text-neutral-400 border-white/10 bg-white/5",
};

function StatusIcon({ status }) {
  if (status === "active") return <Radio className="w-3.5 h-3.5 text-monk-critical" />;
  if (status === "cold") return <Snowflake className="w-3.5 h-3.5 text-cyan-300" />;
  return <CircleDot className="w-3.5 h-3.5 text-neutral-500" />;
}

export default function CommandCenter() {
  const [cases, setCases] = useState([]);
  const [engines, setEngines] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = () => {
    Promise.all([api.get("/cases"), api.get("/engines")])
      .then(([c, e]) => { setCases(c.data); setEngines(e.data.engines); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const stats = useMemo(() => ({
    total: cases.length,
    active: cases.filter((c) => c.status === "active").length,
    critical: cases.filter((c) => c.priority === "critical").length,
    activeEngines: engines.filter((e) => e.active).length,
  }), [cases, engines]);

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">Investigative Reasoning · Milestone 1</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tighter leading-none">COMMAND CENTER</h1>
        </div>
        <NewCaseDialog onCreated={load} />
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden mb-8">
        {[
          { label: "Open Cases", value: stats.total },
          { label: "Active Investigations", value: stats.active, accent: "text-monk-critical" },
          { label: "Critical Priority", value: stats.critical, accent: "text-monk-warn" },
          { label: "Engines Online", value: `${stats.activeEngines}/${engines.length}`, accent: "text-monk-blue" },
        ].map((s, i) => (
          <div key={i} className="bg-monk-panel p-5" data-testid="stat-card">
            <div className="overline mb-2">{s.label}</div>
            <div className={`font-heading font-800 text-3xl tracking-tighter ${s.accent || "text-white"}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Case grid */}
        <div className="xl:col-span-2">
          <div className="overline mb-3">Case Files</div>
          {loading ? (
            <div className="font-mono text-monk-blue text-sm">LOADING CASE FILES…</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {cases.map((c) => (
                <button key={c.id} onClick={() => nav(`/case/${c.id}`)} data-testid="case-card"
                  className="group text-left bg-monk-panel border border-white/10 rounded-md p-4 hover:border-monk-blue/40 hover:bg-monk-panel/80 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <StatusIcon status={c.status} />
                      <span className="overline">{c.case_type?.replace("_", " ")}</span>
                    </div>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${PRIORITY_STYLES[c.priority]}`}>{c.priority?.toUpperCase()}</span>
                  </div>
                  <h3 className="font-heading font-700 text-lg tracking-tight leading-tight mb-2 group-hover:text-monk-blue transition-colors">{c.title}</h3>
                  <p className="text-[13px] text-neutral-400 line-clamp-2 mb-3 leading-relaxed">{c.summary}</p>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-[11px] text-neutral-500 font-mono">
                      <MapPin className="w-3 h-3" /> {c.location || "—"}
                    </span>
                    <span className="flex items-center gap-1 text-[11px] text-monk-blue opacity-0 group-hover:opacity-100 transition-opacity">
                      OPEN <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Engine registry */}
        <div>
          <div className="overline mb-3 flex items-center gap-2"><Cpu className="w-3.5 h-3.5" /> Engine Registry · {engines.length} Registered</div>
          <div className="bg-monk-panel border border-white/10 rounded-md max-h-[640px] overflow-y-auto" data-testid="engine-grid">
            {engines.map((e) => (
              <div key={e.key} className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 last:border-0" title={e.reason || ""} data-testid={`engine-row-${e.key}`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${e.active ? "bg-monk-blue" : "bg-neutral-700"}`} />
                <div className="flex-1 min-w-0">
                  <span className={`block text-[12px] leading-tight ${e.active ? "text-white" : "text-neutral-500"}`}>{e.name}</span>
                  {e.status === "partial" && (
                    <span className="block text-[10px] leading-snug text-neutral-500 mt-0.5" data-testid={`engine-functions-${e.key}`}>
                      built: {e.built_functions.join(", ")} · not built: {e.unbuilt_functions.join(", ")}
                    </span>
                  )}
                </div>
                <span className="text-[9px] font-mono shrink-0 text-neutral-600 uppercase" data-testid={`engine-tier-${e.key}`}>{e.tier}</span>
                <span className={`text-[9px] font-mono shrink-0 uppercase ${e.active ? "text-monk-blue" : "text-neutral-600"}`} data-testid={`engine-status-${e.key}`}>{e.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
